import { useMemo } from "react";
import { PurchaseComponentsContext } from "@contexts/PurchaseComponentsContext";
import { useCookies } from "@hooks/useCookies";

import { QueryClientProvider } from "@tanstack/react-query";

import { createQueryClient } from "@api/cache";
import { createApiClient } from "@api/client";

import type { PurchaseComponentsProviderProps } from "./PurchaseComponentsProvider.types";

const queryClient = createQueryClient();

const DEFAULT_CONTENTFUL_SPACE_ID = "i7ba51j76wte";

const resolveContentfulConfig = ({ contentful }: Pick<PurchaseComponentsProviderProps, "contentful">) => {
  if (!contentful.accessToken) {
    throw new Error("PurchaseComponentsProvider requires contentful.accessToken to be provided by the consuming app.");
  }

  if (!contentful.environment) {
    throw new Error("PurchaseComponentsProvider requires contentful.environment to be provided by the consuming app.");
  }

  return {
    accessToken: contentful.accessToken,
    environment: contentful.environment,
    spaceId: contentful.spaceId ?? DEFAULT_CONTENTFUL_SPACE_ID,
  };
};

/** Sets up the shared API client, Contentful config and query client. */
export const PurchaseComponentsProvider = ({
  apiUrl,
  getAccessToken,
  contentful,
  children,
}: PurchaseComponentsProviderProps) => {
  const { partner, locale } = useCookies();
  const resolvedContentful = useMemo(() => resolveContentfulConfig({ contentful }), [contentful]);

  const contextValue = useMemo(
    () => ({
      apiClient: createApiClient({ baseURL: apiUrl, partner, getAccessToken }),
      partner,
      locale,
      contentful: resolvedContentful,
    }),
    [apiUrl, getAccessToken, locale, partner, resolvedContentful],
  );

  return (
    <PurchaseComponentsContext.Provider value={contextValue}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </PurchaseComponentsContext.Provider>
  );
};

export type { PurchaseComponentsProviderProps } from "./PurchaseComponentsProvider.types";
